"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { AlertTriangle, Bot, CreditCard, Loader2, LogOut, Sparkles } from "lucide-react";
import { useUser } from "@/features/auth/hooks/useUser";
import { useAuthModal } from "@/features/auth/hooks/useAuthModal";
import { ConfirmLogoutModal } from "@/features/auth/components/ConfirmLogoutModal";
import {
  API_LIMITS,
  FREE_PLAN,
  PLAN_CONFIG,
  formatUsd,
  intervalSuffix,
  isPaidPlanId,
} from "@/lib/billing/planConfig";
import type { BillingState } from "@/lib/billing/types";
import { useBilling, type ActionResult } from "../hooks/useBilling";
import { apiCardFor } from "../lib/apiCard";
import { formatBillingDate } from "../lib/format";
import { PricingPlans } from "./PricingPlans";
import { BTN_PRIMARY, BTN_SECONDARY, PLAN_ACCENT } from "./planStyle";

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <section className={`rounded-xl border border-[#2a475e] bg-[#131c28] p-5 md:p-6 ${className}`}>{children}</section>
  );
}

function StatusBadge({ state }: { state: BillingState }) {
  // A plain free account has no status worth a badge ("Free FREE").
  if (state.plan === "free" && state.status !== "past_due") return null;
  const [text, color] = state.complimentary
    ? ["Complimentary", "#a4d007"]
    : state.status === "past_due"
      ? ["Payment issue", "#e35e1c"]
      : state.status === "scheduled_cancel"
        ? ["Won't renew", "#e35e1c"]
        : state.plan === "free"
          ? ["Free", "#8f98a0"]
          : ["Active", "#a4d007"];
  return (
    <span
      className="rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
      style={{ color, borderColor: `${color}55`, backgroundColor: `${color}14` }}
    >
      {text}
    </span>
  );
}

function useAction() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function run(key: string, fn: () => Promise<ActionResult> | void) {
    setBusy(key);
    setError(null);
    const r = await fn();
    setBusy(null);
    if (r && !r.ok) setError(r.message);
  }
  return { busy, error, run };
}

function PlanSummary({ state }: { state: BillingState }) {
  const billing = useBilling();
  const { busy, error, run } = useAction();
  const paid = isPaidPlanId(state.plan) ? state.plan : null;
  const sub = state.subscription;
  const accent = PLAN_ACCENT[paid ?? "free"];

  // While a downgrade is pending Creem already bills the LOWER product, so
  // its price is the next renewal's, not the plan the user holds.
  const pending = state.pendingChange;
  const price =
    pending && paid
      ? `${PLAN_CONFIG[paid].prices[pending.interval].display}${intervalSuffix(pending.interval)}`
      : sub?.priceCents != null && sub.interval
        ? `${formatUsd(sub.priceCents)}${intervalSuffix(sub.interval)}`
        : paid && !state.complimentary
          ? `${PLAN_CONFIG[paid].prices.month.display}/mo`
          : null;

  let line: React.ReactNode = null;
  if (state.complimentary) {
    line = "Your plan was set up by the team — there's nothing to pay.";
  } else if (state.status === "past_due") {
    line = (
      <span className="text-[#f0a37c]">
        We couldn&apos;t take your last payment. Update your payment method to keep {paid ? PLAN_CONFIG[paid].label : "your plan"}.
      </span>
    );
  } else if (state.status === "scheduled_cancel" && paid) {
    line = (
      <>
        Canceled — you keep {PLAN_CONFIG[paid].label} until <strong className="text-white">{formatBillingDate(sub?.currentPeriodEnd ?? state.planExpiresAt)}</strong>.
        You won&apos;t be charged again.
      </>
    );
  } else if (pending && paid) {
    line = (
      <>
        Switches to {PLAN_CONFIG[pending.plan].label} (
        {PLAN_CONFIG[pending.plan].prices[pending.interval].display}
        {intervalSuffix(pending.interval)}) on{" "}
        <strong className="text-white">{formatBillingDate(pending.effectiveAt)}</strong>; until then you keep
        everything in {PLAN_CONFIG[paid].label}.
      </>
    );
  } else if (paid && sub?.nextChargeAt) {
    line = (
      <>
        Renews on <strong className="text-white">{formatBillingDate(sub.nextChargeAt)}</strong>
        {sub.priceCents != null ? ` for ${formatUsd(sub.priceCents)} plus any tax` : ""}.
      </>
    );
  } else if (!paid) {
    line = "Chart history up to 3 months, the watchlist, compare and the hype board.";
  }

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-[#8f98a0]">Current plan</p>
          <div className="mt-1.5 flex items-center gap-3">
            <h2 className="text-2xl font-semibold" style={{ color: paid ? accent : "#e8eef3" }}>
              {paid ? PLAN_CONFIG[paid].label : FREE_PLAN.label}
            </h2>
            <StatusBadge state={state} />
          </div>
          {price && <p className="mt-1 font-[family-name:var(--font-geist-mono)] text-sm text-[#c7d5e0]">{price}</p>}
        </div>
      </div>

      {line && <p className="mt-4 text-sm leading-relaxed text-[#c7d5e0]">{line}</p>}

      {state.subscriptionUnavailable && (
        <p className="mt-3 flex items-center gap-2 text-xs text-[#f0a37c]">
          <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          Our payment provider didn&apos;t answer, so renewal details may be missing. Refresh in a minute.
        </p>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {state.status === "past_due" && (
          <button type="button" className={BTN_PRIMARY} style={{ backgroundColor: "#e35e1c", color: "#fff" }} disabled={busy !== null} onClick={() => run("portal", billing.openPortal)}>
            {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <CreditCard className="h-4 w-4" aria-hidden />}
            Update payment method
          </button>
        )}
        {state.status === "scheduled_cancel" && (
          <button type="button" className={BTN_PRIMARY} disabled={busy !== null} onClick={() => run("resume", billing.resume)}>
            {busy === "resume" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Resume subscription
          </button>
        )}
        {state.pendingChange && paid && state.status !== "scheduled_cancel" && (
          <button
            type="button"
            className={BTN_PRIMARY}
            disabled={busy !== null}
            onClick={() => run("undo", () => billing.choosePlan(paid, state.pendingChange!.interval, "account_undo"))}
          >
            Keep {PLAN_CONFIG[paid].label}
          </button>
        )}
        {!paid && (
          <button
            type="button"
            className={BTN_PRIMARY}
            onClick={() => document.getElementById("plans")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          >
            <Sparkles className="h-4 w-4" aria-hidden />
            See paid plans
          </button>
        )}
        {state.canOpenPortal && state.status !== "past_due" && (
          <button type="button" className={BTN_SECONDARY} disabled={busy !== null} onClick={() => run("portal", billing.openPortal)}>
            {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <CreditCard className="h-4 w-4" aria-hidden />}
            Payment method &amp; invoices
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}

      {paid && !state.complimentary && state.status !== "scheduled_cancel" && state.status !== "canceled" && (
        <p className="mt-5 border-t border-[#2a475e]/60 pt-4 text-xs text-[#8f98a0]">
          Don&apos;t need it anymore?{" "}
          <button type="button" onClick={() => billing.openCancel("account_page")} className="text-[#c7d5e0] underline underline-offset-2 hover:text-white">
            Cancel subscription
          </button>{" "}
          — you keep {PLAN_CONFIG[paid].label} until the end of the period you paid for.
        </p>
      )}

    </Card>
  );
}

function UsageMeter({
  title,
  unit,
  meter,
  notIncluded,
}: {
  title: string;
  unit: string;
  meter: { used: number; limit: number | null; resetsAt: string };
  notIncluded: string;
}) {
  const { used, limit, resetsAt } = meter;
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div>
      <h3 className="text-base font-semibold text-white">{title}</h3>
      {limit === null ? (
        <p className="mt-2 text-sm text-[#c7d5e0]">Unlimited (team account) · {used} used this month</p>
      ) : limit === 0 ? (
        <p className="mt-2 text-sm text-[#c7d5e0]">{notIncluded}</p>
      ) : (
        <>
          <div className="mt-3 flex items-baseline justify-between text-sm">
            <span className="font-[family-name:var(--font-geist-mono)] text-white">
              {used} <span className="text-[#8f98a0]">of {limit}</span>
            </span>
            <span className="text-xs text-[#8f98a0]">Resets {formatBillingDate(resetsAt)}</span>
          </div>
          <div
            className="mt-2 h-2 overflow-hidden rounded-full bg-[#1f2e3f]"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={limit}
            aria-valuenow={used}
            aria-label={`${unit} used this month`}
          >
            <div
              className="h-full rounded-full"
              style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#e35e1c" : "#66c0f4" }}
            />
          </div>
        </>
      )}
    </div>
  );
}

function UsageCard({ state }: { state: BillingState }) {
  const showScans = state.audienceScans.limit !== 0 || state.plan === "pro";
  return (
    <Card>
      <p className="text-xs font-medium uppercase tracking-wider text-[#8f98a0]">This month</p>
      <div className="mt-1.5 space-y-5">
        <UsageMeter
          title="AI credits"
          unit="AI credits"
          meter={state.aiCredits}
          notIncluded={`Not included in Free. ${PLAN_CONFIG.pro.label} includes ${PLAN_CONFIG.pro.aiCreditsPerMonth} a month, ${PLAN_CONFIG.studio.label} ${PLAN_CONFIG.studio.aiCreditsPerMonth}.`}
        />
        {showScans && (
          <UsageMeter
            title="Audience scans"
            unit="Audience scans"
            meter={state.audienceScans}
            notIncluded={`Part of ${PLAN_CONFIG.studio.label}: ${PLAN_CONFIG.studio.audienceScansPerMonth} scans a month.`}
          />
        )}
        <p className="text-xs leading-relaxed text-[#8f98a0]">
          An AI review report costs 1 credit (Standard), 2 (Deep) or 4 (Max) — a game with
          fewer reviews than the depth reads is charged for what the AI actually reads.
          Unused credits don&apos;t roll over.
        </p>
      </div>
    </Card>
  );
}

/** The way to the API dashboard on /account, for Studio and admins (see apiCardFor). */
function ApiAccessCard() {
  const units = API_LIMITS.unitsPerMonth.toLocaleString("en-US");
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Bot className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#2dd4bf]" aria-hidden />
          <div>
            <h2 className="text-sm font-semibold text-white">API access</h2>
            <p className="mt-1 text-sm text-[#8f98a0]">
              Your keys, the MCP server and API usage ({units} units a month across your keys) live on the Agentic
              dashboard.
            </p>
          </div>
        </div>
        <Link href="/agentic" className={BTN_SECONDARY}>
          Open the dashboard
        </Link>
      </div>
    </Card>
  );
}

export function AccountPage() {
  const { user, plan, loading, serverEmail } = useUser();
  const { open: openAuth } = useAuthModal();
  const billing = useBilling();
  const { state, load, error } = billing;
  const [logoutOpen, setLogoutOpen] = useState(false);
  const signOutRef = useRef<HTMLFormElement>(null);
  const signedIn = !!user || plan !== "anonymous";
  const userId = user?.id ?? null;

  // Fresh numbers whenever the page opens — this is where people check them.
  useEffect(() => {
    if (userId) void load({ force: true });
  }, [userId, load]);

  if (loading) {
    return <div className="h-64 animate-pulse rounded-xl border border-[#2a475e] bg-[#131c28]" />;
  }

  if (!signedIn) {
    return (
      <Card className="text-center">
        <h2 className="text-lg font-semibold text-white">Sign in to see your account</h2>
        <p className="mt-2 text-sm text-[#8f98a0]">Your plan, billing and usage live here.</p>
        <button type="button" onClick={openAuth} className={`${BTN_PRIMARY} mt-5`}>
          Sign in
        </button>
      </Card>
    );
  }

  const email = user?.email || serverEmail || "";
  const apiCard = apiCardFor(plan, user?.email);

  return (
    <div className="space-y-5">
      {state ? (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <PlanSummary state={state} />
          <UsageCard state={state} />
        </div>
      ) : error ? (
        <Card>
          <p className="text-sm text-red-300">{error}</p>
          <button type="button" className={`${BTN_SECONDARY} mt-3`} onClick={() => load({ force: true })}>
            Try again
          </button>
        </Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div className="h-56 animate-pulse rounded-xl border border-[#2a475e] bg-[#131c28]" />
          <div className="h-56 animate-pulse rounded-xl border border-[#2a475e] bg-[#131c28]" />
        </div>
      )}

      {apiCard && <ApiAccessCard />}

      {state && !state.complimentary && (
        <section id="plans" aria-labelledby="change-plan-heading" className="scroll-mt-24 pt-4">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
            <h2 id="change-plan-heading" className="text-lg font-semibold text-white">
              {state.plan === "free" ? "Upgrade" : "Change plan"}
            </h2>
            <Link href="/pricing#compare" className="text-xs text-[#66c0f4] hover:underline">
              Compare every feature →
            </Link>
          </div>
          <PricingPlans source="account_page" variant="compact" />
        </section>
      )}

      <Card>
        <p className="text-xs font-medium uppercase tracking-wider text-[#8f98a0]">Account</p>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-white">{email}</p>
            <p className="mt-0.5 text-xs text-[#8f98a0]">Signed in with this address</p>
          </div>
          <button type="button" className={BTN_SECONDARY} onClick={() => setLogoutOpen(true)}>
            <LogOut className="h-4 w-4" aria-hidden />
            Sign out
          </button>
        </div>
        <p className="mt-5 border-t border-[#2a475e]/60 pt-4 text-xs leading-relaxed text-[#8f98a0]">
          Want your account and data deleted? Write to{" "}
          <a
            href="mailto:support@steampeek.app?subject=Delete%20my%20account"
            className="text-[#66c0f4] hover:underline"
          >
            support@steampeek.app
          </a>{" "}
          from this address — cancel any subscription first so you aren&apos;t charged again.
        </p>
      </Card>

      <ConfirmLogoutModal
        isOpen={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        onConfirm={() => signOutRef.current?.requestSubmit()}
      />
      <form ref={signOutRef} action="/api/auth/sign-out" method="post" className="hidden" />
    </div>
  );
}
